/**
 * Reusable audit-reason capture modal.
 *
 * Every audit-critical mutation needs an `X-Reason` (API_CONTRACT §5). This
 * modal collects it (min 3 chars), surfaces any ApiError inline, and calls
 * `onConfirm(reason)`. The parent owns open/close + the mutation; it typically
 * flips `opened` to false on success.
 */

import { isApiError } from "@compliance/domain";
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

export function ReasonModal({
  opened,
  title,
  description,
  confirmLabel = "Confirm",
  confirmColor = "indigo",
  busy,
  error,
  onConfirm,
  onClose,
  onAbort,
}: {
  opened: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  confirmColor?: string;
  busy: boolean;
  error?: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  /**
   * Optional: abort the in-flight request. When provided, the Cancel button
   * stays enabled while `busy` so a slow/hung mutation can be cancelled (BUG-3b).
   * Without it, Cancel is disabled mid-flight (the original behaviour).
   */
  onAbort?: () => void;
}) {
  const [reason, setReason] = useState("");
  // No header-safety check any more: `apiFetch` encodes a reason that cannot
  // travel as raw bytes (RFC 8187) instead of refusing it, so an operator can
  // write in any language. Length is the only rule left.
  const reasonValid = reason.trim().length >= 3;

  // Reset whenever the modal closes so a reused instance never reopens with a
  // stale reason that would mislabel a different audit event.
  useEffect(() => {
    if (!opened) setReason("");
  }, [opened]);

  const close = () => {
    if (busy) {
      // Mid-flight: abort if the caller wired a canceller, else keep blocking
      // close (don't orphan an un-cancellable request).
      if (!onAbort) return;
      onAbort();
    }
    setReason("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={title}
      centered
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
    >
      <Stack gap="md">
        {description ? (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              {isApiError(error) ? `${error.code}: ${error.message}` : (error as Error)?.message}
            </Text>
            {isApiError(error) ? (
              <Text size="xs" c="dimmed" mt={4} ff="monospace">
                request_id {error.requestId}
              </Text>
            ) : null}
          </Alert>
        ) : null}
        <Textarea
          label="Reason"
          description="Recorded in the audit log. Minimum 3 characters."
          minRows={3}
          maxRows={6}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonValid ? "Reason must be at least 3 characters" : null}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={busy && !onAbort}>
            {busy && onAbort ? "Cancel request" : "Cancel"}
          </Button>
          <Button
            color={confirmColor}
            disabled={!reasonValid || busy}
            loading={busy}
            onClick={() => onConfirm(reason.trim())}
            data-tour="reason-modal-confirm"
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
